/**
 * The High quality renderer: a deferred-ish SSAO pipeline layered on top of
 * the same mesh Low draws. Four passes per frame:
 *
 *   1. G-buffer prepass  → view-space normal + linear depth + view position (MRT)
 *   2. dual-radius SSAO  → single-channel AO (at SSAO_PARAMS.resScale)
 *   3. depth-aware blur  → H then V, no bleeding across depth edges
 *   4. forward shade     → over the background (the drafting grid), the scene light, AO multiplied into its ambient term only, + outlines + glass
 *
 * The light is Low's, from sceneLighting.ts: fixed in view space, so the
 * model tumbles under a stationary light. Only the ambient occlusion sets
 * the two qualities apart.
 *
 * Shares the WebGL2 context + vertex buffer owned by GLRenderer (see `litShare`)
 * so both qualities draw the exact same geometry without a second upload.
 */

import { PALETTE } from '../editor-model';
import { type Mat4 } from '../math3d';
import {
  AO_SPEC,
  BLUR_FRAG,
  compileProgram,
  createNoiseTexture,
  createTarget,
  deleteTarget,
  FLAT_FRAG,
  FLAT_VERT,
  FULLSCREEN_VERT,
  GBUFFER_FRAG,
  GBUFFER_SPECS,
  GBUFFER_VERT,
  GL,
  hemisphereKernel,
  LIT_FRAG,
  LIT_VERT,
  makeFullscreenVAO,
  SSAO_FRAG,
  SSAO_PARAMS,
  Target,
  uniforms,
} from './lit';
import { type Surface, surfaceShows } from './mesh3d';
import { applySceneLight } from './sceneLighting';

const NOISE_SIZE = 4;
const MAX_KERNEL = 64;

type Uni = Record<string, WebGLUniformLocation | null>;

export class LitRenderer {
  readonly supported: boolean;
  private gl: GL;
  private vbo: WebGLBuffer;
  private ibo: WebGLBuffer;
  private uintIndex: boolean;
  private getSurfaces: () => Surface[];

  private geomVAO: WebGLVertexArrayObject;
  private fsVAO: WebGLVertexArrayObject;
  private noise: WebGLTexture;
  private kernel = hemisphereKernel(MAX_KERNEL);

  private gbuffer!: Target;
  private aoA!: Target;
  private aoB!: Target;
  private fbW = 0;
  private fbH = 0;
  private aoScale = 0;

  private gbufferProg: WebGLProgram;
  private uGbuffer: Uni;
  private litProg: WebGLProgram;
  private uLit: Uni;
  private flatProg: WebGLProgram;
  private uFlat: Uni;
  private ssaoProg: WebGLProgram;
  private uSsao: Uni;
  private blurProg: WebGLProgram;
  private uBlur: Uni;

  constructor(gl: GL, vbo: WebGLBuffer, uintIndex: boolean, getSurfaces: () => Surface[]) {
    this.gl = gl;
    this.vbo = vbo;
    this.uintIndex = uintIndex;
    this.getSurfaces = getSurfaces;
    // Every lit target is RGBA16F or R8 — never full float. WebGL2 browsers
    // gate 16F renderability behind EXT_color_buffer_float; native GLES
    // drivers (expo-gl — Apple's driver never exposes the full-float
    // extension) gate it behind EXT_color_buffer_half_float. Either suffices.
    this.supported = !!gl.getExtension('EXT_color_buffer_float') || !!gl.getExtension('EXT_color_buffer_half_float');
    this.ibo = gl.createBuffer()!;
    this.noise = createNoiseTexture(gl);
    this.fsVAO = makeFullscreenVAO(gl);

    this.gbufferProg = compileProgram(gl, GBUFFER_VERT, GBUFFER_FRAG);
    this.uGbuffer = uniforms(gl, this.gbufferProg, ['uView', 'uProj']);
    this.litProg = compileProgram(gl, LIT_VERT, LIT_FRAG);
    this.uLit = uniforms(gl, this.litProg, ['uView', 'uProj', 'uAO', 'uResolution', 'uAoStrength']);
    applySceneLight(gl, this.litProg);
    this.flatProg = compileProgram(gl, FLAT_VERT, FLAT_FRAG);
    this.uFlat = uniforms(gl, this.flatProg, ['uViewProj', 'uAlpha']);
    this.ssaoProg = compileProgram(gl, FULLSCREEN_VERT, SSAO_FRAG);
    this.uSsao = uniforms(gl, this.ssaoProg, [
      'uNormalDepth',
      'uPos',
      'uNoise',
      'uProj',
      'uNoiseScale',
      'uKernel',
      'uSamples',
      'uNearRadius',
      'uFarRadius',
      'uNearWeight',
      'uFarWeight',
      'uPower',
      'uRangeFalloff',
    ]);
    this.blurProg = compileProgram(gl, FULLSCREEN_VERT, BLUR_FRAG);
    this.uBlur = uniforms(gl, this.blurProg, ['uAO', 'uNormalDepth', 'uTexel']);

    this.geomVAO = this.buildGeomVAO();
  }

  /** A VAO binding the shared interleaved VBO ([pos, normal, color]) + our IBO. */
  private buildGeomVAO(): WebGLVertexArrayObject {
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    const stride = 9 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 3, gl.FLOAT, false, stride, 24);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bindVertexArray(null);
    return vao;
  }

  private ensureTargets(w: number, h: number, scale: number): void {
    if (this.fbW === w && this.fbH === h && this.aoScale === scale) return;
    const gl = this.gl;
    if (this.gbuffer) {
      deleteTarget(gl, this.gbuffer);
      deleteTarget(gl, this.aoA);
      deleteTarget(gl, this.aoB);
    }
    const aw = Math.max(1, Math.round(w * scale)), ah = Math.max(1, Math.round(h * scale));
    this.gbuffer = createTarget(gl, w, h, GBUFFER_SPECS(gl), true);
    this.aoA = createTarget(gl, aw, ah, AO_SPEC(gl), false);
    this.aoB = createTarget(gl, aw, ah, AO_SPEC(gl), false);
    this.fbW = w;
    this.fbH = h;
    this.aoScale = scale;
  }

  /** Gather index lists for the currently-visible geometry (mirrors GLRenderer). */
  private gather(visible: boolean[]): { fills: number[]; lines: number[]; glass: number[] } {
    const fills: number[] = [], lines: number[] = [], glass: number[] = [];
    for (const s of this.getSurfaces()) {
      if (!surfaceShows(s, visible)) continue;
      push(fills, s.fillTris);
      push(lines, s.lines);
      push(glass, s.glassTris);
    }
    return { fills, lines, glass };
  }

  private draw(idx: number[], mode: number): void {
    if (idx.length === 0) return;
    const gl = this.gl;
    const arr = this.uintIndex ? new Uint32Array(idx) : new Uint16Array(idx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, arr, gl.DYNAMIC_DRAW);
    gl.drawElements(mode, idx.length, this.uintIndex ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT, 0);
  }

  private fullscreen(): void {
    this.gl.bindVertexArray(this.fsVAO);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
  }

  /** Draw a High frame; `drawBackground` draws behind the scene once the screen is cleared to the stage's paper (the drafting grid). */
  render(viewProj: Mat4, view: Mat4, proj: Mat4, visible: boolean[], w: number, h: number, drawBackground: () => void): void {
    const gl = this.gl;
    const p = SSAO_PARAMS;
    this.ensureTargets(w, h, p.resScale);
    const { fills, lines, glass } = this.gather(visible);

    // --- Pass 1: G-buffer prepass (opaque fills only) ---
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.gbuffer.fbo);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 0); // depth (.w) clears to 0 ⇒ background
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(gl.CCW);
    gl.useProgram(this.gbufferProg);
    gl.uniformMatrix4fv(this.uGbuffer.uView, false, view);
    gl.uniformMatrix4fv(this.uGbuffer.uProj, false, proj);
    gl.bindVertexArray(this.geomVAO);
    this.draw(fills, gl.TRIANGLES);

    // --- Pass 2: SSAO ---
    const aw = this.aoA.width, ah = this.aoA.height;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.aoA.fbo);
    gl.viewport(0, 0, aw, ah);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    gl.useProgram(this.ssaoProg);
    bindTex(gl, 0, this.gbuffer.textures[0]);
    gl.uniform1i(this.uSsao.uNormalDepth, 0);
    bindTex(gl, 1, this.gbuffer.textures[1]);
    gl.uniform1i(this.uSsao.uPos, 1);
    bindTex(gl, 2, this.noise);
    gl.uniform1i(this.uSsao.uNoise, 2);
    gl.uniformMatrix4fv(this.uSsao.uProj, false, proj);
    gl.uniform2f(this.uSsao.uNoiseScale, aw / NOISE_SIZE, ah / NOISE_SIZE);
    gl.uniform3fv(this.uSsao.uKernel, this.kernel);
    gl.uniform1i(this.uSsao.uSamples, Math.max(1, Math.min(MAX_KERNEL, Math.round(p.samples))));
    gl.uniform1f(this.uSsao.uNearRadius, p.nearRadius);
    gl.uniform1f(this.uSsao.uFarRadius, p.farRadius);
    gl.uniform1f(this.uSsao.uNearWeight, p.nearWeight);
    gl.uniform1f(this.uSsao.uFarWeight, p.farWeight);
    gl.uniform1f(this.uSsao.uPower, p.power);
    gl.uniform1f(this.uSsao.uRangeFalloff, p.rangeFalloff);
    this.fullscreen();

    // --- Pass 3: depth-aware separable blur (H: A→B, V: B→A) ---
    gl.useProgram(this.blurProg);
    bindTex(gl, 1, this.gbuffer.textures[0]);
    gl.uniform1i(this.uBlur.uNormalDepth, 1);
    this.blurAxis(this.aoA, this.aoB, 1 / aw, 0);
    this.blurAxis(this.aoB, this.aoA, 0, 1 / ah);
    const finalAO = this.aoA;

    // --- Pass 4: composite to the default framebuffer, over the background ---
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.clearColor(...PALETTE.stage, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.bindVertexArray(null);
    drawBackground();

    // Lit fills, AO into ambient. Polygon offset so outlines sit cleanly on top.
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.frontFace(gl.CCW);
    gl.enable(gl.POLYGON_OFFSET_FILL);
    gl.polygonOffset(1.2, 1.5);
    gl.useProgram(this.litProg);
    gl.uniformMatrix4fv(this.uLit.uView, false, view);
    gl.uniformMatrix4fv(this.uLit.uProj, false, proj);
    gl.uniform2f(this.uLit.uResolution, w, h);
    gl.uniform1f(this.uLit.uAoStrength, p.aoStrength);
    bindTex(gl, 0, finalAO.textures[0]);
    gl.uniform1i(this.uLit.uAO, 0);
    gl.bindVertexArray(this.geomVAO);
    this.draw(fills, gl.TRIANGLES);
    gl.disable(gl.POLYGON_OFFSET_FILL);

    // Outlines, then translucent glass — flat-shaded.
    gl.useProgram(this.flatProg);
    gl.uniformMatrix4fv(this.uFlat.uViewProj, false, viewProj);
    gl.uniform1f(this.uFlat.uAlpha, 1);
    this.draw(lines, gl.LINES);
    if (glass.length) {
      gl.uniform1f(this.uFlat.uAlpha, 0.4);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.depthMask(false);
      this.draw(glass, gl.TRIANGLES);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
    }
    gl.bindVertexArray(null);
  }

  private blurAxis(src: Target, dst: Target, tx: number, ty: number): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, dst.width, dst.height);
    bindTex(gl, 0, src.textures[0]);
    gl.uniform1i(this.uBlur.uAO, 0);
    gl.uniform2f(this.uBlur.uTexel, tx, ty);
    this.fullscreen();
  }
}

function bindTex(gl: GL, unit: number, tex: WebGLTexture): void {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
}

function push(dst: number[], src: number[]): void {
  for (let i = 0; i < src.length; i++) dst.push(src[i]);
}
